import { ApiResponse, PaginatedResponse } from "../../types/common";
import { Employee, EmployeeFormData, EmployeeProfileResponse, FetchEmployeesParams } from "../../types/employees";
import { axiosInstance } from "./axios";

export const employeeService = {
  getAll: async (params: FetchEmployeesParams): Promise<PaginatedResponse<Employee>> => {
    const response = await axiosInstance.get<PaginatedResponse<Employee>>('v1/Employees', {
      params: {
        InstituteId: params.instituteId,
        Page: params.page ?? 1,
        Limit: params.limit ?? 15,
        ...(params.search ? { Search: params.search } : {}),
      },
    });
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to fetch employees');
    }
    return response.data;
  },
  getAllUnpaged: async (instituteId: number): Promise<Employee[]> => {
    const pageSize = 200;
    const first = await employeeService.getAll({ instituteId, page: 1, limit: pageSize });
    if (!first.hasNextPage) return first.data ?? [];
    const pages = [first.data ?? []];
    const totalPages = Math.max(first.totalPages || 1, 1);
    for (let page = 2; page <= totalPages; page += 1) {
      const next = await employeeService.getAll({ instituteId, page, limit: pageSize });
      pages.push(next.data ?? []);
    }
    return pages.flat();
  },
  getHostelEmployees: async (instituteId: number): Promise<Employee[]> => 
  {
    const response = await axiosInstance.get<ApiResponse<Employee[]>>(`v1/Employees/GetAllHostelEmployees?InstituteId=${instituteId}`);
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to Add employee');
    }
    return response.data.data;
  },
  getById: async (id: number) => {
    const response = await axiosInstance.get<ApiResponse<Employee>>(`v1/Employees/${id}`);
    if (!response.data.succeeded) {
     throw new Error(response.data.messages?.[0] || 'failed to Add employee');
   }
   return response.data.data;
  },
  getProfile: async (id: number): Promise<EmployeeProfileResponse> => {
    const response = await axiosInstance.get<ApiResponse<EmployeeProfileResponse>>(`v1/Employees/${id}`);
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to fetch employee profile');
    }
    return response.data.data;
  },
  create: async (data: EmployeeFormData, instituteId: number) => {
    const response = await axiosInstance.post<ApiResponse<number>>('v1/Employees', {
      ...data,
      instituteId: instituteId
    }, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to save employee');
    }
  },
  update: async (id:number,data: EmployeeFormData, instituteId: number) => {
    const response = await axiosInstance.post<ApiResponse<number>>('v1/Employees', {
      ...data,
      id: id,
      instituteId: instituteId
    }, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to save employee');
    }
  },
  delete : async (id:number) => {
    const response = await axiosInstance.delete<ApiResponse<boolean>>('v1/Employees',{
      params: {
        EmployeeId: id
      }
    });
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to delete employee');
    }
  },
  generateAppointmentLetterPreview: async (instituteId : number, data: EmployeeFormData) => {
    try {
      const annualCtc = data.annualCtc === "" || data.annualCtc === null || data.annualCtc === undefined
        ? null
        : Number(data.annualCtc);
      const response = await axiosInstance.post<Blob>(`v1/Employees/preview-letter`, {
        ...data,
        instituteId,
        annualCtc
      },
        {
          responseType: 'blob',
        });
      return response.data; // PDF or similar file content as Blob
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
          const parsed = JSON.parse(errorText);
          throw new Error(parsed.messages?.[0] || 'Failed to generate preview');
        } catch {
          throw new Error('Unexpected error while generating preview');
        }
      }
      throw new Error(error.message || 'Failed to generate preview');
    }
  },
  getAppointmentLetterUrl: async (id: number) => {
    const response = await axiosInstance.get<ApiResponse<string>>(`v1/Employees/${id}/appointment-letter-url`);
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to generate appointment preview');
    }
    return response.data.data;
  },
  generateAppointmentLetter: async (id: number) => {
    const response = await axiosInstance.get<ApiResponse<string>>(`v1/Employees/${id}/generate-appointment-letter-url`);
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to generate appointment letter');
    }
    return response.data.data;
  },
  sendAppointmentLetter: async (id: number) => {
    const response = await axiosInstance.post<ApiResponse<boolean>>(`v1/Employees/${id}/send-appointment-letter`, {
      employeeId: id
    });
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'failed to send appointment letter');
    }
    return response.data.data;
  }
}